import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { MAX_PRESET_NAME_LENGTH } from './useCabinPresets';
import type {
  QuickPresetDefinition,
  QuickPresetId,
  SavedCabinPreset,
} from './cabinEditorTypes';

export interface PresetBarProps {
  readonly quickPresets: readonly QuickPresetDefinition[];
  /** Presets saved for the aircraft type being edited. */
  readonly savedPresets: readonly SavedCabinPreset[];
  /** Whether the current draft may be saved (it has no errors). */
  readonly canSave: boolean;
  readonly onApplyQuick: (id: QuickPresetId) => void;
  readonly onApplySaved: (preset: SavedCabinPreset) => void;
  readonly onDeleteSaved: (id: string) => void;
  /** Returns true when the preset was stored. */
  readonly onSave: (name: string) => boolean;
}

/** Quick preset buttons, the player's saved presets and the save-as-preset form. */
export function PresetBar({
  quickPresets,
  savedPresets,
  canSave,
  onApplyQuick,
  onApplySaved,
  onDeleteSaved,
  onSave,
}: PresetBarProps): ReactElement {
  const uid = useId();
  const [name, setName] = useState('');
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canSave) {
      return;
    }
    const trimmed = name.trim();
    if (onSave(trimmed)) {
      setSavedNotice(`Saved “${trimmed}”.`);
      setName('');
    }
  };

  return (
    <section className="ce-card" aria-labelledby={`${uid}-title`}>
      <h3 id={`${uid}-title`} className="ce-card__title">
        Presets
      </h3>

      <div className="ce-presets" role="group" aria-label="Quick presets">
        {quickPresets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="ce-preset"
            onClick={(): void => onApplyQuick(preset.id)}
          >
            <span className="ce-preset__label">{preset.label}</span>
            <span className="ce-preset__desc">{preset.description}</span>
          </button>
        ))}
      </div>

      {savedPresets.length > 0 ? (
        <div className="ce-saved">
          <h4 className="ce-metric-block__title">Your presets for this aircraft</h4>
          <ul className="ce-saved__list">
            {savedPresets.map((preset) => (
              <li key={preset.id} className="ce-saved__item">
                <button
                  type="button"
                  className="ce-btn ce-btn--ghost ce-saved__apply"
                  onClick={(): void => onApplySaved(preset)}
                >
                  {preset.name}
                </button>
                <button
                  type="button"
                  className="ce-btn ce-btn--icon"
                  aria-label={`Delete preset ${preset.name}`}
                  onClick={(): void => onDeleteSaved(preset.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <form className="ce-save" onSubmit={handleSubmit}>
        <label className="ce-save__label" htmlFor={`${uid}-name`}>
          Save current layout as preset
        </label>
        <div className="ce-save__row">
          <input
            id={`${uid}-name`}
            className="ce-text"
            type="text"
            value={name}
            maxLength={MAX_PRESET_NAME_LENGTH}
            placeholder="Preset name"
            onChange={(event): void => {
              setName(event.target.value);
              setSavedNotice(null);
            }}
          />
          <button type="submit" className="ce-btn" disabled={!canSave || name.trim().length === 0}>
            Save as Preset
          </button>
        </div>
        {!canSave ? (
          <p className="ce-save__hint">Fix the validation errors to save this layout.</p>
        ) : null}
        <p className="ce-save__hint" role="status">
          {savedNotice}
        </p>
      </form>
    </section>
  );
}
